<script setup lang="ts">
/**
 * Histogramme sobre (SVG, sans bibliothèque) : barres arrondies, barre choisie en bleu avec
 * sa valeur au-dessus, les autres en gris. Toucher une barre la sélectionne.
 * Pour les lecteurs d'écran : un résumé et la liste des valeurs, cachés visuellement.
 */
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue'

import { formatMoney } from '@/domain'

import { amountsHidden } from '../privacy'

const props = withDefaults(
  defineProps<{
    label: string
    data: readonly { label: string; value: number }[]
    height?: number
    /** `xof` : montants (masqués en mode discret) ; `count` : nombres simples. */
    unit?: 'xof' | 'count'
  }>(),
  { height: 168, unit: 'xof' },
)

const selected = defineModel<number | null>('selected', { default: null })

/**
 * Le graphique se dessine à sa largeur réelle : textes et barres gardent leur taille sur
 * téléphone comme sur grand écran (pas d'étirement du SVG). Barres plafonnées à 48 px de large.
 */
const container = useTemplateRef<HTMLElement>('container')
const width = ref(320)
let observer: ResizeObserver | null = null

onMounted(() => {
  if (!container.value) return
  width.value = container.value.clientWidth || 320
  if (typeof ResizeObserver === 'undefined') return
  observer = new ResizeObserver(([entry]) => {
    if (entry) width.value = Math.max(Math.round(entry.contentRect.width), 160)
  })
  observer.observe(container.value)
})
onBeforeUnmount(() => observer?.disconnect())

const WIDTH = computed(() => width.value)
const MAX_BAR = 48
const LABEL_SPACE = 22
const VALUE_SPACE = 28
const GAP_RATIO = 0.32

const max = computed(() => Math.max(...props.data.map((point) => point.value), 1))
const slot = computed(() => WIDTH.value / Math.max(props.data.length, 1))
const barWidth = computed(() => Math.min(slot.value * (1 - GAP_RATIO), MAX_BAR))
const plotHeight = computed(() => props.height - LABEL_SPACE - VALUE_SPACE)

const bars = computed(() =>
  props.data.map((point, index) => {
    const height = Math.max(
      (Math.max(point.value, 0) / max.value) * plotHeight.value,
      point.value > 0 ? 4 : 2,
    )
    const x = index * slot.value + (slot.value - barWidth.value) / 2
    return { ...point, index, x, height, y: VALUE_SPACE + plotHeight.value - height }
  }),
)

const COUNT = new Intl.NumberFormat('fr-FR')

function display(value: number): string {
  if (props.unit === 'count') return COUNT.format(value)
  return amountsHidden.value ? String.fromCharCode(0x2022).repeat(5) : formatMoney(value)
}

const active = computed(() =>
  selected.value === null ? undefined : bars.value.find((bar) => bar.index === selected.value),
)
</script>

<template>
  <figure ref="container" class="flex flex-col">
    <svg
      :viewBox="`0 0 ${WIDTH} ${height}`"
      :width="WIDTH"
      :height="height"
      class="block overflow-visible"
      role="img"
      :aria-label="label"
    >
      <g v-for="bar in bars" :key="bar.index">
        <rect
          :x="bar.x"
          :y="bar.y"
          :width="barWidth"
          :height="bar.height"
          :rx="Math.min(8, barWidth / 2)"
          :class="[
            'transition-colors duration-160',
            bar.index === selected ? 'fill-accent-strong' : 'fill-ink-disabled',
          ]"
        />
        <text
          :x="bar.x + barWidth / 2"
          :y="height - 6"
          text-anchor="middle"
          :class="['text-caption', bar.index === selected ? 'fill-ink' : 'fill-ink-muted']"
        >
          {{ bar.label }}
        </text>
        <rect
          :x="bar.index * slot"
          y="0"
          :width="slot"
          :height="height"
          fill="transparent"
          class="cursor-pointer"
          @click="selected = bar.index === selected ? null : bar.index"
        />
      </g>
      <text
        v-if="active"
        :x="Math.min(Math.max(active.x + barWidth / 2, 40), WIDTH - 40)"
        :y="Math.max(active.y - 10, 16)"
        text-anchor="middle"
        class="fill-ink text-label tabular-nums"
      >
        {{ display(active.value) }}
      </text>
    </svg>
    <figcaption class="sr-only">
      <ul>
        <li v-for="point in data" :key="point.label">
          {{ point.label }} : {{ display(point.value) }}
        </li>
      </ul>
    </figcaption>
  </figure>
</template>
